// index.tsx
import * as React from 'react';
import { HomePageProps } from '../../types/navigationProperties';
import { PaginatedResponse } from '../../types/pagination';
import { Project } from '../../services/ProjectsServiceProvider';
import { useTranslation } from 'react-i18next';
import { Box, Typography, Tooltip, Button, Stack, Zoom } from '@mui/material';
import { Parallax, ParallaxLayer, IParallax } from '@react-spring/parallax'
import Footer from '../../components/Footer';
import { ColorfulText } from '../../components/ColorfulText';
import logoBlackTp from '../../assets/img/logo_black_tp.png';
import homepageTp from '../../assets/img/homepage_tp2.png';
import segedaHome from '../../assets/img/segeda_home.png';
import guitarServiceHome from '../../assets/img/guitar_service_home.png';
import customGuitarHome from '../../assets/img/custom_guitar_home.png';
import {
    Link as RouteLink
} from "react-router-dom";
import { LightShotSingle } from '../../components/LightShotSingle';
import { GalleryImage } from '../../types/galleryImage';
import { SetButtonCallbackFunction } from '../../types/callback';
import { pulse } from '../../components/NavigationAppBar';
import { SEO } from '../../components/SEO';
import { MetaTag } from '../../services/SEOServiceProvider';

export default function Home({ props, setPageName, metaTags }: { props: HomePageProps, setPageName: SetButtonCallbackFunction, metaTags: MetaTag[] }) {
    const { t } = useTranslation();
    const projects: PaginatedResponse<Project> | undefined = props.projects;
    const parallax = React.useRef<IParallax>(null!);
    const [rotate, setRotate] = React.useState(0);
    const PAGES = 4;
    const PAGES_MOBILE = 5.1;
    const alignCenter = { display: 'flex', alignItems: 'center' };
    const alignFooter = { display: 'block', width: '100vw' };
    const [open, setOpen] = React.useState(false);
    const [selectedImage, setSelectedImage] = React.useState<GalleryImage>();
    const [metaTag, setMetaTag] = React.useState<MetaTag>();

    const handleOpenLightShot = (image: string, name: string) => {
        let item: GalleryImage = { src: image, name: name };
        setSelectedImage(item);
        setOpen(true);
    };

    React.useEffect(() => {
        setPageName('home');
        let metaTagData = metaTags !== undefined && metaTags.filter((item: MetaTag) => item.page === 'home' );
        if(metaTagData && metaTagData.length > 0) {
            let homePageMetaTag: MetaTag = {...metaTagData[0]} as MetaTag;
            homePageMetaTag.url = window.location.href;
            homePageMetaTag.image = logoBlackTp;
            // homePageMetaTag.metas = [];
            //console.log('check meta tag for home page');
            //console.log(homePageMetaTag);
            //console.log(metaTagData);
            setMetaTag(homePageMetaTag);
        }

        const handleScroll = (e: Event) => {
            const height = parallax.current.space;
            const scrollablePages = PAGES - 1; // because you can't scroll past the last page
            const scrollHeight = height * scrollablePages;
            const targetElement = e.target as HTMLElement;
            const scrollTop = targetElement.scrollTop;
            const percentScrolled = scrollTop / scrollHeight;
            const currentPage = Math.floor(percentScrolled * scrollablePages);
            const currentPageScrollTop = scrollTop - (height * (currentPage));
            const currentPagePercent = currentPageScrollTop / height;

            // because the ParallaxLayer below has an `offset` of `0`
            if (currentPage === 0) {
                setRotate(currentPagePercent);
            }
        }

        const container = parallax.current.container.current;
        container.addEventListener('scroll', handleScroll);

        return () => {
            container.removeEventListener('scroll', handleScroll);
        }
    }, [setPageName, metaTags]);

    return (
        <Box>
            <SEO metaTag={metaTag} />
            <LightShotSingle image={selectedImage} opened={open} setOpenLightShot={setOpen} />
            <Box style={{ width: '100%', height: '100%', background: 'white' }} sx={{ display: { xs: 'none', sm: 'none', md: 'none', lg: 'flex' }}}>
                <Parallax ref={parallax} pages={PAGES}>
                    <ParallaxLayer offset={0} speed={1} style={{ backgroundColor: 'white' }} />
                    <ParallaxLayer offset={1} speed={1} style={{ backgroundColor: 'white' }} />
                    <ParallaxLayer offset={2} speed={1} style={{ backgroundColor: 'white' }} />
                    <ParallaxLayer offset={3} speed={1} style={{ backgroundColor: 'white' }} />
                    <ParallaxLayer offset={4} speed={1} style={{ backgroundColor: 'white' }} />
                    <Box>
                        <ParallaxLayer offset={-0.1150} speed={-0.008025} style={{ ...alignCenter, justifyContent: 'center' }}>
                            <Typography sx={{ fontSize: { sm: '16px', md: '20px', lg: '22px' } }}>
                                {t("home.top")}
                            </Typography>
                        </ParallaxLayer>
                        <ParallaxLayer offset={-0.0810} speed={-0.012025} style={{ ...alignCenter, justifyContent: 'center', width: '70vw' }}>
                            <Typography sx={{ fontSize: { sm: '16px', md: '20px', lg: '22px' } }}>
                                {t("home.left")}
                            </Typography>
                        </ParallaxLayer>
                        <ParallaxLayer offset={-0.0500} speed={-0.016025} style={{ ...alignCenter, justifyContent: 'center', width: '130vw' }}>
                            <Typography sx={{ fontSize: { sm: '16px', md: '20px', lg: '22px' } }}>
                                {t("home.right")}
                            </Typography>
                        </ParallaxLayer>
                        <ParallaxLayer offset={0.0550} speed={-0.012500} style={{ ...alignCenter, justifyContent: 'center' }}>
                            <div style={{ ...alignCenter }}>
                                <Box sx={{ animation: `${pulse} 1s ease-in-out infinite` }}>
                                    <img loading="lazy" src={logoBlackTp} alt="oboss logo"
                                        style={{ transform: `rotate(${rotate * 360}deg)` }} />
                                </Box>
                            </div>
                        </ParallaxLayer>
                        <ParallaxLayer offset={1.0250} speed={0.1} style={{ ...alignCenter, justifyContent: 'center' }}>
                            <div style={{ ...alignCenter }}>
                                <img loading="lazy" src={homepageTp} alt="oboss guitar" onClick={() => handleOpenLightShot(homepageTp, "oboo guitar")} />
                                <Box>
                                    <ColorfulText style={{ ...alignCenter, justifyContent: 'center', paddingBottom: '40px', paddingRight: '40px' }}
                                        text={t("home.heading.1")} randomChars={true} skipChar={[]} useColor={'random'} />
                                    <ColorfulText style={{ ...alignCenter, justifyContent: 'center', paddingBottom: '40px', paddingRight: '40px' }}
                                        text={t("home.heading.2")} randomChars={true} skipChar={[]} useColor={'random'} />
                                    <ColorfulText style={{ ...alignCenter, justifyContent: 'center', paddingBottom: '40px', paddingRight: '40px' }}
                                        text={t("home.heading.3")} randomChars={true} skipChar={[]} useColor={'random'} />
                                    <ColorfulText style={{ ...alignCenter, justifyContent: 'center', paddingBottom: '40px', paddingRight: '40px' }}
                                        text={t("home.heading.4")} randomChars={true} skipChar={[]} useColor={'random'} />
                                {/*
                                    <Typography style={{ ...alignCenter, justifyContent: 'center', paddingBottom: '40px', paddingRight: '40px' }}>
                                        {t("home.heading.1")}
                                    </Typography>
                                    <Typography style={{ ...alignCenter, justifyContent: 'center', paddingBottom: '40px', paddingRight: '40px' }}>
                                        {t("home.heading.2")}
                                    </Typography>
                                    <Typography style={{ ...alignCenter, justifyContent: 'center', paddingBottom: '40px', paddingRight: '40px' }}>
                                        {t("home.heading.3")}
                                    </Typography>
                                    <Typography style={{ ...alignCenter, justifyContent: 'center', paddingBottom: '300px', paddingRight: '40px' }}>
                                        {t("home.heading.4")}
                                    </Typography>
                                */}
                                </Box>
                            </div>
                        </ParallaxLayer>
                        <ParallaxLayer offset={1.4850} speed={-0.016025} style={{ ...alignCenter, justifyContent: 'center', width: '130vw' }}>
                            <div style={{ ...alignCenter }}>
                                <img loading="lazy" src={segedaHome} alt="segeda project" onClick={() => handleOpenLightShot(segedaHome, "segeda project")} />
                            </div>
                        </ParallaxLayer>
                        <ParallaxLayer offset={1.8950} speed={-0.016025} style={{ ...alignCenter, justifyContent: 'center', width: '130vw' }}>
                            {projects !== undefined && projects.results.map((item) => (
                                <div style={{ ...alignCenter }} key={item.slug}>
                                    <Stack direction="column" spacing={2} key={item.slug}>
                                        <RouteLink to={`projects/${item.slug}`}>
                                            <Tooltip title={t('home.view') + " " + item.title.value}
                                                     placement="bottom"
                                                     slots={{
                                                        transition: Zoom,
                                                     }}
                                                     slotProps={{
                                                        transition: { timeout: 300 },
                                                     }} arrow>
                                                <Button color="secondary"
                                                        style={{ width: '200px'}}
                                                        variant='outlined'
                                                        size='medium'>
                                                            {item.title.value}
                                                </Button>
                                            </Tooltip>
                                        </RouteLink>
                                    </Stack>
                                </div>
                            ))}
                        </ParallaxLayer>
                        <ParallaxLayer offset={2.1850} speed={-0.016025} style={{ ...alignCenter, justifyContent: 'center', width: '70vw', height: '100px' }}>
                            <div style={{ ...alignCenter }}>
                                <Box>
                                    <Typography style={{ ...alignCenter, justifyContent: 'center', paddingBottom: '20px' }}>
                                        {t("home.heading.5")}
                                    </Typography>
                                    <Typography style={{ ...alignCenter, justifyContent: 'center', paddingBottom: '40px' }}>
                                        {t("home.heading.6")}
                                    </Typography>
                                </Box>
                            </div>
                        </ParallaxLayer>
                        <ParallaxLayer offset={2.4650} speed={-0.110025} style={{ ...alignCenter, justifyContent: 'center', width: '60vw', height: '300px' }}>
                            <div style={{ ...alignCenter }}>
                                <img loading="lazy" style={{ width: '500px' }} src={customGuitarHome} alt="custom guitars" onClick={() => handleOpenLightShot(customGuitarHome, "custom guitars")}/>
                            </div>
                        </ParallaxLayer>
                        <ParallaxLayer offset={2.3550} speed={-0.110025} style={{ ...alignCenter, justifyContent: 'center', width: '60vw', zIndex: 2 }}>
                            <div style={{ ...alignCenter }}>
                                <Stack direction="column" spacing={2}>
                                    <RouteLink to="/custom-shop">
                                        <Tooltip title={t('home.view') + " " + t("custom_shop.menu.link")}
                                                 placement="bottom"
                                                 slots={{
                                                    transition: Zoom,
                                                 }}
                                                 slotProps={{
                                                    transition: { timeout: 300 },
                                                 }} arrow>
                                            <Button color="secondary"
                                                    style={{ width: '200px'}}
                                                    variant='outlined'
                                                    size='medium'>
                                                {t('custom_shop.menu.link')}
                                            </Button>
                                        </Tooltip>
                                    </RouteLink>
                                </Stack>
                            </div>
                        </ParallaxLayer>
                        <ParallaxLayer offset={2.5850} speed={-0.110025} style={{ ...alignCenter, justifyContent: 'center', width: '125vw', height: '100px' }}>
                            <div style={{ ...alignCenter }}>
                                <Box>
                                    <Typography style={{ ...alignCenter, justifyContent: 'center', paddingBottom: '20px' }}>
                                        {t("home.heading.7")}
                                    </Typography>
                                    <Typography style={{ ...alignCenter, justifyContent: 'center', paddingBottom: '40px' }}>
                                        {t("home.heading.8")}
                                    </Typography>
                                </Box>
                            </div>
                        </ParallaxLayer>
                        <ParallaxLayer offset={2.8950} speed={-0.016025} style={{ ...alignCenter, justifyContent: 'center', width: '125vw', height: '300px' }}>
                            <div style={{ ...alignCenter }}>
                                <img loading="lazy" style={{ width: '500px' }} src={guitarServiceHome} alt="guitar service" onClick={() => handleOpenLightShot(guitarServiceHome, "guitar service")}/>
                            </div>
                        </ParallaxLayer>
                        <ParallaxLayer offset={3.1850} speed={-0.016025} style={{ ...alignCenter, justifyContent: 'center', width: '120vw', height: '200px', zIndex: 3 }}>
                            <div style={{ ...alignCenter }}>
                                <Stack direction="column" spacing={2}>
                                    <RouteLink to="/guitar-service">
                                        <Tooltip title={t('home.view') + " " + t("guitar_service.menu.link")}
                                                 placement="bottom"
                                                 slots={{
                                                    transition: Zoom,
                                                 }}
                                                 slotProps={{
                                                    transition: { timeout: 300 },
                                                 }} arrow>
                                            <Button color="secondary"
                                                    style={{ width: '200px'}}
                                                    variant='outlined'
                                                    size='medium'>
                                                {t('guitar_service.menu.link')}
                                            </Button>
                                        </Tooltip>
                                    </RouteLink>
                                </Stack>
                            </div>
                        </ParallaxLayer>
                        <ParallaxLayer offset={3.0350} speed={-0.016025} style={{ ...alignCenter, justifyContent: 'center', width: '60vw', height: '100px' }}>
                            <div style={{ ...alignCenter }}>
                                <Box>
                                    <Typography style={{ ...alignCenter, justifyContent: 'center', paddingBottom: '20px' }}>
                                        {t("home.heading.9")}
                                    </Typography>
                                    <Typography style={{ ...alignCenter, justifyContent: 'center', paddingBottom: '40px' }}>
                                        {t("home.heading.10")}
                                    </Typography>
                                </Box>
                            </div>
                        </ParallaxLayer>
                        <ParallaxLayer offset={3.4050} speed={-0.016025} style={{ ...alignCenter, justifyContent: 'center', height: '610px' }}>
                            <div style={{ ...alignFooter }}>
                                <Box>
                                    <Footer {...props} />
                                </Box>
                            </div>
                        </ParallaxLayer>
                    </Box>
                </Parallax>
            </Box>

            <Box style={{ width: '100%', height: '100%', background: 'white' }} sx={{ display: { md: 'flex', lg: 'none' }}}>
                <Parallax ref={parallax} pages={PAGES_MOBILE}>
                    <ParallaxLayer offset={0} speed={1} style={{ backgroundColor: 'white' }} />
                    <ParallaxLayer offset={1} speed={1} style={{ backgroundColor: 'white' }} />
                    <ParallaxLayer offset={2} speed={1} style={{ backgroundColor: 'white' }} />
                    <ParallaxLayer offset={3} speed={1} style={{ backgroundColor: 'white' }} />
                    <ParallaxLayer offset={4} speed={1} style={{ backgroundColor: 'white' }} />
                    <Box>
                        <ParallaxLayer offset={-0.1050} speed={-0.02025} style={{ ...alignCenter, justifyContent: 'center' }}>
                            <Typography sx={{ fontSize: { sm: '16px', md: '20px', lg: '22px' } }}>
                                {t("home.top")}
                            </Typography>
                        </ParallaxLayer>
                        <ParallaxLayer offset={-0.1050} speed={-0.06025} style={{ ...alignCenter, justifyContent: 'center' }}>
                            <Typography sx={{ fontSize: { sm: '16px', md: '20px', lg: '22px' } }}>
                                {t("home.left")}
                            </Typography>
                        </ParallaxLayer>
                        <ParallaxLayer offset={-0.1050} speed={-0.10025} style={{ ...alignCenter, justifyContent: 'center' }}>
                            <Typography sx={{ fontSize: { sm: '16px', md: '20px', lg: '22px' } }}>
                                {t("home.right")}
                            </Typography>
                        </ParallaxLayer>
                        <ParallaxLayer offset={0.1250} speed={-0.12500} style={{ ...alignCenter, justifyContent: 'center' }}>
                            <div style={{ ...alignCenter }}>
                                <Box sx={{ animation: `${pulse} 1s ease-in-out infinite` }}>
                                    <img loading="lazy" src={logoBlackTp} alt="oboss logo"
                                        style={{ transform: `rotate(${rotate * 360}deg)` }} />
                                </Box>
                            </div>
                        </ParallaxLayer>
                        <ParallaxLayer offset={0.7650} speed={0.1} style={{ ...alignCenter, justifyContent: 'center' }}>
                            <div style={{ ...alignCenter, padding: '20px' }}>
                                <Box>
                                    <Typography style={{ ...alignCenter, justifyContent: 'center', paddingBottom: '10px' }}>
                                        {t("home.heading.1")}
                                    </Typography>
                                    <Typography style={{ ...alignCenter, justifyContent: 'center', paddingBottom: '20px' }}>
                                        {t("home.heading.2")}
                                    </Typography>
                                </Box>
                            </div>
                        </ParallaxLayer>
                        <ParallaxLayer offset={1.4000} speed={0.1} style={{ ...alignCenter, justifyContent: 'center' }}>
                            <div style={{ ...alignCenter }}>
                                <img loading="lazy" src={homepageTp} alt="oboss guitar" />
                            </div>
                        </ParallaxLayer>
                        <ParallaxLayer offset={2.0000} speed={0.1} style={{ ...alignCenter, justifyContent: 'center' }}>
                            <div style={{ ...alignCenter, padding: '20px' }}>
                                <Box>
                                    <Typography style={{ ...alignCenter, justifyContent: 'center', paddingBottom: '10px' }}>
                                        {t("home.heading.3")}
                                    </Typography>
                                    <Typography style={{ ...alignCenter, justifyContent: 'center', paddingBottom: '20px' }}>
                                        {t("home.heading.4")}
                                    </Typography>
                                </Box>
                            </div>
                        </ParallaxLayer>
                        <ParallaxLayer offset={2.5950} speed={-0.016025} style={{ ...alignCenter, justifyContent: 'center', height: '100px' }}>
                            <div style={{ ...alignCenter }}>
                                <Box>
                                    <Typography style={{ ...alignCenter, justifyContent: 'center', paddingBottom: '10px' }}>
                                        {t("home.heading.5")}
                                    </Typography>
                                    <Typography style={{ ...alignCenter, justifyContent: 'center', paddingBottom: '20px' }}>
                                        {t("home.heading.6")}
                                    </Typography>
                                </Box>
                            </div>
                        </ParallaxLayer>
                        <ParallaxLayer offset={2.4300} speed={-0.016025} style={{ ...alignCenter, justifyContent: 'center' }}>
                            <div style={{ ...alignCenter }}>
                                <img loading="lazy" src={segedaHome} alt="segeda project" style={{ width: '300px' }}/>
                            </div>
                        </ParallaxLayer>
                        <ParallaxLayer offset={2.6950} speed={-0.016025} style={{ ...alignCenter, justifyContent: 'center' }}>
                            {projects?.results?.map((item) => (
                                <div style={{ ...alignCenter }} key={item.slug}>
                                    <Stack direction="column" spacing={2} key={item.slug}>
                                        <RouteLink to={`projects/${item.slug}`}>
                                            <Tooltip title={t('home.view') + " " + item.title.value}
                                                     placement="bottom"
                                                     slots={{
                                                        transition: Zoom,
                                                     }}
                                                     slotProps={{
                                                        transition: { timeout: 300 },
                                                     }} arrow>
                                                <Button color="secondary"
                                                        style={{ width: '200px'}}
                                                        variant='outlined'
                                                        size='medium'>
                                                            {item.title.value}
                                                </Button>
                                            </Tooltip>
                                        </RouteLink>
                                    </Stack>
                                </div>
                            ))}
                        </ParallaxLayer>
                        <ParallaxLayer offset={2.9450} speed={0.10025} style={{ ...alignCenter, justifyContent: 'center' }}>
                            <div style={{ ...alignCenter }}>
                                <Box>
                                    <Typography style={{ ...alignCenter, justifyContent: 'center', paddingBottom: '10px' }}>
                                        {t("home.heading.7")}
                                    </Typography>
                                    <Typography style={{ ...alignCenter, justifyContent: 'center' }}>
                                        {t("home.heading.8")}
                                    </Typography>
                                </Box>
                            </div>
                        </ParallaxLayer>
                        <ParallaxLayer offset={3} speed={0.10025} style={{ ...alignCenter, justifyContent: 'center' }}>
                            <div style={{ ...alignCenter }}>
                                <img loading="lazy" style={{ width: '300px' }} src={customGuitarHome} alt="custom guitars"/>
                            </div>
                        </ParallaxLayer>
                        <ParallaxLayer offset={3.1450} speed={0.10025} style={{ ...alignCenter, justifyContent: 'center' }}>
                            <div style={{ ...alignCenter }}>
                                <Stack direction="column" spacing={2}>
                                    <RouteLink to="/custom-shop">
                                        <Tooltip title={t('home.view') + " " + t("custom_shop.menu.link")}
                                                 placement="bottom"
                                                 slots={{
                                                    transition: Zoom,
                                                 }}
                                                 slotProps={{
                                                    transition: { timeout: 300 },
                                                 }} arrow>
                                            <Button color="secondary"
                                                    style={{ width: '200px'}}
                                                    variant='outlined'
                                                    size='medium'>
                                                {t('custom_shop.menu.link')}
                                            </Button>
                                        </Tooltip>
                                    </RouteLink>
                                </Stack>
                            </div>
                        </ParallaxLayer>
                        <ParallaxLayer offset={3.2950} speed={-0.016025} style={{ ...alignCenter, justifyContent: 'center' }}>
                            <div style={{ ...alignCenter }}>
                                <Box>
                                    <Typography style={{ ...alignCenter, justifyContent: 'center', paddingBottom: '10px' }}>
                                        {t("home.heading.9")}
                                    </Typography>
                                    <Typography style={{ ...alignCenter, justifyContent: 'center', paddingBottom: '20px' }}>
                                        {t("home.heading.10")}
                                    </Typography>
                                </Box>
                            </div>
                        </ParallaxLayer>
                        <ParallaxLayer offset={3.4450} speed={-0.016025} style={{ ...alignCenter, justifyContent: 'center' }}>
                            <div style={{ ...alignCenter }}>
                                <img loading="lazy" style={{ width: '300px' }} src={guitarServiceHome} alt="guitar service"/>
                            </div>
                        </ParallaxLayer>
                        <ParallaxLayer offset={3.5950} speed={-0.016025} style={{ ...alignCenter, justifyContent: 'center' }}>
                            <div style={{ ...alignCenter }}>
                                <Stack direction="column" spacing={2}>
                                    <RouteLink to="/guitar-service">
                                        <Tooltip title={t('home.view') + " " + t("guitar_service.menu.link")}
                                                 placement="bottom"
                                                 slots={{
                                                    transition: Zoom,
                                                 }}
                                                 slotProps={{
                                                    transition: { timeout: 300 },
                                                 }} arrow>
                                            <Button color="secondary"
                                                    style={{ width: '200px'}}
                                                    variant='outlined'
                                                    size='medium'>
                                                {t('guitar_service.menu.link')}
                                            </Button>
                                        </Tooltip>
                                    </RouteLink>
                                </Stack>
                            </div>
                        </ParallaxLayer>
                        <ParallaxLayer offset={4.1850} speed={-0.076025} style={{ ...alignCenter, justifyContent: 'center' }}>
                            <div style={{ ...alignFooter }}>
                                <Box>
                                    <Footer {...props} />
                                </Box>
                            </div>
                        </ParallaxLayer>
                    </Box>
                </Parallax>
            </Box>
        </Box>
    );
}